import Audience from '@components/audience/Audience'
import { useHasAccess } from '@components/HasAccess'
import LoyaltyTier from '@components/loyaltyTier/LoyaltyTier'
import { Notification } from '@components/notifications/Notification'
import Page from '@components/Page/Page'
import { TableUrlParams, useTableUrlSync } from '@hooks/useTableUrlSync'
import { Notification as NotificationModel } from '@models/Notification'
import { deleteNotification } from '@services/NotificationService'
import { getAudiences } from '@store/AudiencesReducer'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { getLoyaltyTiers } from '@store/LoyaltyTierReducer'
import {
  getNotifications,
  selectNotifications,
  setNotificationsQuery,
} from '@store/NotificationReducer'
import {
  initialQuery,
  SBTable,
  TableParams,
} from '@supplement-bacon/sb-table-filters'
import { ProfileOutlined } from '@ant-design/icons'
import { SBRM, SBRMAction } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { Permission } from '@utils/permissions'
import {
  Button,
  Modal,
  Space,
  TableColumnsType,
  Tooltip,
  Typography,
} from 'antd'
import { useEffect, useState } from 'react'

const { Text } = Typography

const PushNotificationsPage = () => {
  const { hasAccess } = useHasAccess()
  const dispatch = useAppDispatch()
  const notifications = useAppSelector(selectNotifications())
  const { query, isLoading } = useAppSelector((state) => state.notification)

  const [selectedNotification, setSelectedNotification] = useState<
    NotificationModel | undefined
  >(undefined)

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [viewOnly, setViewOnly] = useState<boolean>(false)

  const onClose = () => {
    setIsOpen(false)
    setSelectedNotification(undefined)
    if (viewOnly) {
      setViewOnly(false)
    }
  }

  const toUpdateNotif = (record: NotificationModel) => {
    setSelectedNotification(record)
    setIsOpen(true)
  }

  const toViewNotif = (record: NotificationModel) => {
    setSelectedNotification(record)
    setViewOnly(true)
    setIsOpen(true)
  }

  const deleteNotif = (record: NotificationModel) => {
    Modal.confirm({
      title: 'Are you sure you want to delete ?',
      content: `Notification : ${record.name}`,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        deleteNotification(record).then(() =>
          dispatch(getNotifications(initialQuery))
        )
      },
    })
  }

  const columns: TableColumnsType<NotificationModel> = [
    {
      title: 'Name',
      dataIndex: 'name',
      width: 250,
    },
    {
      title: 'Audience',
      render: (record: NotificationModel) => (
        <Space>
          <Audience.Tag id={record.audience} />
          <LoyaltyTier.Tag id={record.loyaltyTier} />
        </Space>
      ),
    },
    {
      title: 'Analytics',
      width: 400,
      render: (record: NotificationModel) =>
        !record.sentAt || record.sentAt == undefined ? (
          <Text type="secondary">Send the campaign to see the analytics</Text>
        ) : (
          <Notification.Analytics id={record.id} />
        ),
    },
    {
      align: 'right',
      width: 250,
      render: (record: NotificationModel) => (
        <Space>
          <Notification.ActionsColumn
            id={record.id}
            viewNotif={toViewNotif}
            updateNotif={toUpdateNotif}
            deleteNotif={deleteNotif}
            onNotificationSent={() => fetchNotifications(params)}
          />
          <SBRM.Actions
            actions={[SBRMAction.View]}
            entity={Entity.notification}
            entityId={record.id}
            trigger={
              <Tooltip title="Activity log">
                <Button icon={<ProfileOutlined />} />
              </Tooltip>
            }
          />
        </Space>
      ),
    },
  ]

  const fetchNotifications = (params: TableUrlParams) => {
    const newQuery: TableParams = {
      ...query,
      pagination: {
        ...query.pagination,
        current: params.page,
        pageSize: params.pageSize,
      },
    }

    dispatch(setNotificationsQuery(newQuery))
    dispatch(getNotifications(newQuery))

    dispatch(getAudiences(initialQuery))
    dispatch(getLoyaltyTiers(initialQuery))
  }

  const { handleTableChange, params } = useTableUrlSync<NotificationModel>({
    onParamsChange: fetchNotifications,
  })

  useEffect(() => {
    if (isOpen) return

    fetchNotifications(params)
  }, [isOpen])

  return (
    <Page
      permissions={Permission.listPush}
      pageTitle={{
        title: 'Campaigns',
        level: 4,
        toolbar: hasAccess(Permission.createPush) && (
          <Button type="primary" onClick={() => setIsOpen(true)}>
            Create
          </Button>
        ),
      }}
    >
      <Notification.PushWizardModal
        open={isOpen}
        selectedNotification={selectedNotification}
        onReload={() => fetchNotifications(params)}
        onClose={onClose}
        viewOnly={viewOnly}
      />
      <SBTable
        columns={columns}
        dataSource={notifications}
        onChange={handleTableChange}
        pagination={query.pagination}
        loading={isLoading}
        showSorterTooltip={{ title: 'Sort' }}
      />
    </Page>
  )
}

export default PushNotificationsPage
